import {Component, inject} from '@angular/core';
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {FormGroup} from "@angular/forms";
import {CertificateModalService} from "./services/certificate-modal.service";
import {certificateResourceType} from "./configs/certificate-modal-config";
import {
  CertificateData,
  CertificateDialogData,
  CertificateFormField
} from "./types/certificate-modal.types";
import {MatButton, MatMiniFabButton} from "@angular/material/button";
import {defaultCertificateData} from "./configs/default-certificate-data";
import {certificateFormFields} from "./configs/certificate-form-fields";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {MatIcon} from "@angular/material/icon";
import {CertificateFilesStateService} from "./services/certificate-files-state.service";
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {LoadingService} from "../../../../../../core/services/loading.service";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {GetDataInModalParams} from "../../../types/product-card-modals.types";
import {CertificateFormComponent} from "./components/certificate-form/certificate-form.component";
import {CertificateFilesComponent} from "./components/certificate-files/certificate-files.component";

@Component({
  selector: 'app-certificate-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatIcon,
    MatMiniFabButton,
    CertificateFormComponent,
    CertificateFilesComponent
  ],
  templateUrl: './certificate-modal.component.html',
  styleUrl: './certificate-modal.component.scss',
  providers: [ LoadingService, EditModeService ]
})
export class CertificateModalComponent extends BaseModalComponent {
  public certificateDialogData = inject<CertificateDialogData>(MAT_DIALOG_DATA);
  private certificateModalService: CertificateModalService = inject(CertificateModalService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);
  private certificateFilesStateService: CertificateFilesStateService = inject(CertificateFilesStateService);

  public readonly certificateResourceType = certificateResourceType;
  public certificateFormGroups: Array<FormGroup> = [];
  public certificatesData: Array<CertificateData> = [];
  public isValid: boolean = false;
  public noFirstData: boolean = false;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initComponent();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(this.certificateResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<CertificateFormField>(certificateFormFields);
    this.certificateFormGroups.push(formGroup);
    this.certificatesData.push(defaultCertificateData);
    this.isValid = false;
  }

  public updateCertificateData(data: CertificateData, index: number): void {
    this.certificatesData[index] = data;
    this.certificateFormGroups.at(index)?.patchValue(data);
    this.isValid = !!data.id;
    this.noFirstData = !!this.certificatesData.length;
  }

  public deleteCertificate(index: number): void {
    this.certificateFormGroups.splice(index, 1);
    this.certificatesData.splice(index, 1);
    this.isValid = true;
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    await this.deleteRecord(certificateResourceType.path, id)
      .then(() => this.removeCertificateAtIndex(index, id));
  }

  private async initComponent(): Promise<void> {
    this.modalStatesService.openModal(this.certificateResourceType.key);
    await this.fetchCertificateData();
    this.isValid = !!this.certificatesData.at(0)?.id;
    this.noFirstData = !!this.certificatesData.at(0)?.id;
  }

  private async fetchCertificateData(): Promise<void> {
    const params: GetDataInModalParams = {
      path: certificateResourceType.path,
      queryParams: {
        technicalParametersId: Number(this.certificateDialogData.certificates.id)
      }
    };

    this.certificatesData = await this.certificateModalService.getDataInModal<CertificateData>(params).catch(() => []);
    this.initForm();
  }

  private initForm(): void {
    if (!this.certificatesData.length) {
      this.handleAddNew();
      return;
    }

    this.certificateFormGroups = this.certificatesData.map(data => {
      const formGroup = this.baseModalService.getFormGroup<CertificateFormField>(certificateFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });
  }

  private removeCertificateAtIndex(index: number, id: number): void {
    this.certificatesData.splice(index, 1);
    this.certificateFormGroups.splice(index, 1);
    this.certificateFilesStateService.deleteCertificateFilesState(id);

    if (!this.certificatesData.length) {
      this.noFirstData = false;
      this.handleAddNew();
    }
  }
}
